// src/pages/finance-portal/ap-aging/APAgingReport.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Skeleton, Alert, Autocomplete,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

const bucketLabels = { current: 'Current', '1_30': '1-30 Days', '31_60': '31-60 Days', '61_90': '61-90 Days', '90_plus': '90+ Days' };
const bucketColors = { current: '#22c55e', '1_30': '#f59e0b', '31_60': '#f97316', '61_90': '#ef4444', '90_plus': '#dc2626' };

export default function APAgingReport() {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [asOfDate, setAsOfDate] = useState('');
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState(null);

    // Fetch vendors for filter
    useEffect(() => {
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/vendors', { params: { per_page: 500 } });
                if (res.data.success) setVendors(res.data.data || []);
            } catch (err) { /* silent */ }
        })();
    }, []);

    const fetchAging = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = {};
            if (asOfDate) params.as_of_date = asOfDate;
            if (selectedVendor) params.vendor_id = selectedVendor.id;
            const res = await axiosInstance.get('/api/finance/ap-aging', { params });
            if (res.data.success) {
                setData(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load AP aging report');
        } finally {
            setIsLoading(false);
        }
    }, [asOfDate, selectedVendor]);

    useEffect(() => { fetchAging(); }, [fetchAging]);

    const totals = data?.totals || { current: 0, '1_30': 0, '31_60': 0, '61_90': 0, '90_plus': 0, total: 0 };
    const vendorRows = data?.vendors || [];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AssessmentIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            AP Aging Report
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            Outstanding payables by aging bucket
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Filters */}
                <Paper sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField label="As of Date" type="date" value={asOfDate}
                            onChange={e => setAsOfDate(e.target.value)}
                            size="small" sx={{ width: 180 }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        />
                        <Autocomplete
                            options={vendors}
                            value={selectedVendor}
                            onChange={(_, val) => setSelectedVendor(val)}
                            getOptionLabel={(v) => v.name || ''}
                            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                            size="small"
                            sx={{ minWidth: 250 }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="All Vendors"
                                    InputProps={{ ...params.InputProps, sx: { fontFamily, borderRadius: '10px', fontSize: '0.85rem' } }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem' }}>{option.name}</Typography>
                                </li>
                            )}
                        />
                    </Box>
                </Paper>

                {/* Summary Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
                    {Object.entries(bucketLabels).map(([key, label]) => (
                        <Paper key={key} sx={{ p: 2, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
                            <Typography sx={{ fontFamily, fontSize: '1.2rem', fontWeight: 800, color: bucketColors[key], mt: 0.5 }}>
                                {isLoading ? <Skeleton width={80} /> : formatCurrency(totals[key])}
                            </Typography>
                        </Paper>
                    ))}
                </Box>

                {/* Aging Table */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Vendor', 'Current', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days', 'Total'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', textAlign: h !== 'Vendor' ? 'right' : 'left' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [...Array(5)].map((_, i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton height={36} /></TableCell></TableRow>)
                                ) : vendorRows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                            <AssessmentIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No outstanding payables</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <>
                                        {vendorRows.map((row, idx) => (
                                            <TableRow key={row.vendor_id || idx} hover>
                                                <TableCell sx={{ fontFamily, fontWeight: 500, fontSize: '0.85rem', color: '#1e293b' }}>{row.vendor_name || row.vendor}</TableCell>
                                                {['current', '1_30', '31_60', '61_90', '90_plus'].map(bucket => (
                                                    <TableCell key={bucket} sx={{ fontFamily, fontSize: '0.85rem', color: row[bucket] > 0 ? bucketColors[bucket] : '#cbd5e1', textAlign: 'right', fontWeight: row[bucket] > 0 ? 600 : 400 }}>
                                                        {row[bucket] > 0 ? formatCurrency(row[bucket]) : '-'}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', textAlign: 'right' }}>
                                                    {formatCurrency(row.total)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* Totals Row */}
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 800, fontSize: '0.85rem', color: '#1e293b' }}>Total</TableCell>
                                            {['current', '1_30', '31_60', '61_90', '90_plus'].map(bucket => (
                                                <TableCell key={bucket} sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 800, color: totals[bucket] > 0 ? bucketColors[bucket] : '#cbd5e1', textAlign: 'right' }}>
                                                    {totals[bucket] > 0 ? formatCurrency(totals[bucket]) : '-'}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 800, color: '#1e293b', textAlign: 'right' }}>
                                                {formatCurrency(totals.total)}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    );
}
